<template>
  <div class="songwriter-contracts p-t-10">
    <section class="m-b-30" v-if="$can.includes('read-contracts')">
      <div class="level is-marginless">
        <div class="level-left">
          <h3>Contracts</h3>
        </div>
        <div class="level-right">
          <a
            class="is-primary tooltip is-tooltip-left m-r-10"
            :data-tooltip="allPanelsExpanded ? 'Collapse All' : 'Expand All'"
            @click="toggleAllContracts"
            v-if="contracts.length"
          >
            <IconChevronUp v-if="allPanelsExpanded"/>
            <IconChevronDown v-if="!allPanelsExpanded"/>
          </a>
          <a
            class="is-primary tooltip is-tooltip-left"
            data-tooltip="Add Contract"
            @click="create"
            v-if="$can.includes('create-contracts')"
          >
            <IconPlus />
          </a>
        </div>
      </div>
      <div class="level" v-if="!contracts.length">
        <p>
          No contracts.
          <a
            class="m-l-5"
            @click="create"
            v-if="$can.includes('create-contracts')"
            >Add a contract here.</a
          >
        </p>
      </div>

      <!-- Accordion view -->
      <div v-if="contracts.length">
        <div
          class="contract-accordion-item"
          v-for="contract in contracts"
          :key="'collapsed-' + contract.id"
        >
          <!-- Collapsed accordion header - only show when panel is NOT expanded -->
          <div
            v-if="!isPanelExpanded(contract.id)"
            class="contract-accordion-header has-background-light p-3"
            @click="togglePanelExpanded(contract.id)"
            style="cursor: pointer; border: 1px solid #e8e8e8; margin-bottom: 5px;"
          >
            <div class="level is-mobile is-marginless">
              <div class="level-left">
                <div>
                  <p class="has-text-weight-bold">{{ contract.name | orBlank }}</p>
                  <p class="is-size-7 has-text-grey">{{ contract.start_date | momentDate }} - {{ contract.end_date | momentDate }}</p>
                </div>
              </div>
              <div class="level-right">
                <IconChevronDown />
              </div>
            </div>
          </div>
          <!-- Expanded panel content when clicked -->
          <div v-if="isPanelExpanded(contract.id)" class="contract-accordion-content">
            <div class="columns contract-panel is-multiline">
              <div class="column is-3 aside has-background-light">
                <div class="field is-flex is-align-items-center" v-if="$can.includes('update-contracts')">
                  <a class="is-primary m-r-10 tooltip is-tooltip-right" data-tooltip="Collapse" @click.stop="togglePanelExpanded(contract.id)">
                    <IconChevronUp />
                  </a>
                  <a class="is-primary" @click="edit(contract)">Edit Contract</a>
                </div>
                <div class="field">
                  <label class="label">Name</label>
                  <p>{{ contract.name | orBlank }}</p>
                </div>
                <div class="field">
                  <label class="label">Start Date</label>
                  <p>{{ contract.start_date | momentDate }}</p>
                </div>
                <div class="field">
                  <label class="label">End Date</label>
                  <p>{{ contract.end_date | momentDate }}</p>
                </div>
                <div v-if="contract.contracts.length > 0" class="grid">
                  <div class="is-col-min-12">
                    <div class="level mb-0 is-mobile">
                      <div class="level-left">
                        <label class="label">Contracts</label>
                      </div>
                      <div class="level-right">
                        <file-editor
                          :key="contract.id"
                          title="Upload Contract"
                          parentType="contract"
                          :parentId="contract.id"
                          v-on:file-updated="load"
                        ></file-editor>
                        <a
                          class="is-primary tooltip toggle-contract-accordion"
                          :data-tooltip="isContractExpanded(contract.id) ? 'Hide Contract(s)' : 'Show Contract(s)'"
                          @click.stop="toggleContractAccordion(contract.id)"
                          v-if="contract.contracts.length > 0"
                        >
                          <IconChevronUp v-if="isContractExpanded(contract.id)"/>
                          <IconChevronDown v-if="!isContractExpanded(contract.id)"/>
                        </a>
                      </div>
                    </div>
                  </div>
                  <div class="songwriter-contracts-list" v-if="isContractExpanded(contract.id)">
                    <div class="level-left" v-for="file in contract.contracts" :key="file.id">
                      <div class="level-item">
                        <file-editor
                          :key="file.label"
                          title="Upload Contract"
                          :file="file"
                          parentType="contract"
                          :parentId="contract.id"
                          v-on:file-updated="load"
                        ></file-editor>
                      </div>
                    </div>
                  </div>
                </div>
                <div v-else class="grid">
                  <div class="is-col-min-12">
                    <div class="level mb-0 is-mobile">
                      <div class="level-left">
                        <label class="label">Contracts</label>
                      </div>
                      <div class="level-right">
                        <file-editor
                          title="Upload Contract"
                          :file="contract.contract"
                          parentType="contract"
                          :parentId="contract.id"
                        ></file-editor>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div class="column is-9">
                <div class="p-b-30 top-section">
                  <ContractOptionPeriods :contractId="contract.id" />
                </div>
                <div class="p-t-30 p-b-30">
                  <ContractWriterShares :contractId="contract.id" />
                </div>
                <div class="p-t-30 p-b-30">
                  <ContractPublisherShares :contractId="contract.id" />
                </div>
              </div>
              <div class="column is-12" style="border-top: 1px solid #e8e8e8">
                <note-control :notableId="contract.id" notableType="contract"></note-control>
              </div>
            </div>
          </div>
        </div>
      </div>

    </section>

    <form-modal
      type="contract"
      :title="formTitle"
      :model="selectedContract"
      :message="modalMessage"
      :isModal="isModal"
      :canDelete="formCanDelete"
      v-on:clear-modal="clearModal"
      v-on:save-record="save"
      v-on:delete-record="destroy"
    ></form-modal>
  </div>
</template>

<script>
import uiHelper from '@/mixins/uiHelper'
import { IconPlus, IconChevronUp, IconChevronDown } from '@/Icons/IconBase'

import ContractPublisherShares from '@/components/ContractPublisherShares'
import ContractWriterShares from '@/components/ContractWriterShares'
import ContractOptionPeriods from '@/components/ContractOptionPeriods'

import { CONTRACTS_SET } from '@/store/mutations'
import {
  CONTRACT_ALL_FOR_SONGWRITER,
  CONTRACT_UPDATE,
  CONTRACT_CREATE,
  CONTRACT_DELETE
} from '@/store/actions'

export default {
  name: 'SongwriterContracts',
  mixins: [uiHelper],
  components: {
    IconPlus,
    IconChevronUp,
    IconChevronDown,
    ContractPublisherShares,
    ContractWriterShares,
    ContractOptionPeriods
  },

  props: ['songwriterId'],

  data() {
    return {
      formTitle: '',
      formCanDelete: false,
      isModal: false,
      isEditModal: false,
      selectedContract: {},
      isSaving: false,
      modalMessage: '',
      expandedContracts: {},
      expandedPanels: {}
    }
  },

  destroyed: function () {
    this.$store.commit(CONTRACTS_SET, {})
  },

  computed: {
    resourceState() {
      return this.$store.state.contract
    },

    contracts() {
      return this.$store.state.contract.contracts
    },

    allPanelsExpanded() {
      if (!this.contracts || this.contracts.length === 0) return false
      return this.contracts.every(contract => this.expandedPanels[contract.id])
    }
  },

  methods: {
    load() {
      if (this.songwriterId) {
        this.$store.dispatch(CONTRACT_ALL_FOR_SONGWRITER, {
          songwriterId: this.songwriterId,
          data: null
        })
      }
    },

    create() {
      this.clearModal()
      this.formTitle = 'Add Contract'
      this.modalMessage = 'You can add more details later.'
      this.isModal = !this.isModal
    },

    edit(contract) {
      this.formCanDelete = true
      this.formTitle = 'Update Contract'
      this.selectedContract = contract
      this.isModal = !this.isModal
    },

    save(input) {
      if (this.selectedContract.id) {
        this.$store
          .dispatch(CONTRACT_UPDATE, {
            id: this.selectedContract.id,
            data: input,
            songwriterId: this.songwriterId
          })
          .then(() => {
            this.clearModal()
          })
          .catch((err) => {
            let errors = err.response.data.errors
            let messages = []

            if (errors.name) {
              messages.push(errors.name)
            }

            if (errors.start_date) {
              messages.push(errors.start_date)
            }

            if (errors.end_date) {
              messages.push(errors.end_date)
            }
            this.$store.commit('error', messages.join('<br>'))
          })
      } else {
        this.$store
          .dispatch(CONTRACT_CREATE, {
            songwriterId: this.songwriterId,
            data: input
          })
          .then(() => {
            this.clearModal()
          })
          .catch((err) => {
            let errors = err.response.data.errors
            let messages = []

            if (errors.name) {
              messages.push(errors.name)
            }

            if (errors.start_date) {
              messages.push(errors.start_date)
            }

            if (errors.end_date) {
              messages.push(errors.end_date)
            }
            this.$store.commit('error', messages.join('<br>'))
          })
      }
    },

    destroy(contract) {
      this.$store.dispatch(CONTRACT_DELETE, {
        id: contract.id,
        songwriterId: this.songwriterId
      })
      this.clearModal()
    },

    toggleContractAccordion(contractId) {
      this.$set(this.expandedContracts, contractId, !this.expandedContracts[contractId])
    },

    isContractExpanded(contractId) {
      return !!this.expandedContracts[contractId]
    },

    toggleAllContracts() {
      if (this.allPanelsExpanded) {
        // Collapse all
        this.expandedPanels = {}
      } else {
        // Expand all
        const expanded = {}
        this.contracts.forEach(contract => {
          expanded[contract.id] = true
        })
        this.expandedPanels = expanded
      }
    },

    togglePanelExpanded(contractId) {
      this.$set(this.expandedPanels, contractId, !this.expandedPanels[contractId])
    },

    isPanelExpanded(contractId) {
      return !!this.expandedPanels[contractId]
    },

    clearModal() {
      this.formCanDelete = false
      this.formTitle = ''
      this.isModal = false
      this.selectedContract = {}
      this.modalMessage = ''
    }
  }
}
</script>
